import React, { useState, useEffect, useRef } from 'react';
import './GuestAgent.css';

const GuestAgent: React.FC = () => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<{ sender: string; text: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const resetSession = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        await fetch(`${apiUrl}/api/chat/reset`, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (error) {
        console.error('Error resetting session:', error);
      }
    };

    resetSession();
  }, []);

  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      scrollToBottom();
    }
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (message.trim() === '') return;

    const userMessage = { sender: 'user', text: message };
    const newChatHistory = [...chatHistory, userMessage];
    setChatHistory(newChatHistory);
    setMessage('');
    setIsLoading(true); // Set loading to true when message is sent

    // Map the chat history to the format expected by the OpenAI API
    const apiMessages = newChatHistory.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      // For user messages, strip the "Name: " prefix. For agent messages, use the text directly.
      content: msg.text
    }));

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages: apiMessages }),
        credentials: 'include',
      });
      const data = await response.json(); // data is the message object { role, content }
      if (data.reply) {
        let replyText = data.reply;
        try {
          const parsedReply = JSON.parse(replyText);
          if (typeof parsedReply === 'object' && parsedReply.message) {
            replyText = parsedReply.message;
          }
        } catch (e) {
          // Not a JSON string, use it as is
        }
        setChatHistory((prev) => [...prev, { sender: 'agent', text: replyText }]);
      } else if (data.content) {
        setChatHistory((prev) => [...prev, { sender: 'agent', text: data.content }]);
      } else {
        setChatHistory((prev) => [...prev, { sender: 'agent', text: 'Sorry, the agent returned an empty response.' }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory((prev) => [...prev, { sender: 'agent', text: 'Sorry, I am having trouble connecting to the agent.' }]);
    } finally {
      setIsLoading(false); // Set loading to false after response or error
    }
  };

  return (
    <div className="guest-agent-chat-window">
      <div className="chat-messages" ref={chatMessagesRef}>
        {chatHistory.map((msg, index) => (
          <div key={index} className={`message ${msg.sender}`}>
            {msg.text}
          </div>
        ))}
        {isLoading && (
          <div className="message agent loading">
            <div className="typing-indicator">
              <span> </span>
              <span> </span>
              <span> </span>
            </div>
          </div>
        )}
      </div>
      <div className="chat-input-container">
        <input
          type="text"
          placeholder="Ask a question..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }}
        />
        <button onClick={handleSendMessage}>Send</button>
      </div>
    </div>
  );
};

export default GuestAgent;
